import type { Character, Zone } from "@cloudnest/horror-race-engine";
import { HorrorCharacterChip } from "./HorrorCharacterChip";
import { Button } from "./ui/button";

/**
 * T23-b — the active player's coin tray. Draws the same chips as the
 * waiting-player DrawnCoinsPreview, but each chip is a tap target: tap to pick
 * up (gold ring) → the ZoneBoard lights up → tap a zone to temp-place it (ochre
 * +N badge, dimmed). State is owned by GameScreen (usePlacementDraft) so the
 * top ZoneBoard and this tray stay in sync. No +1/+2/+4 radios anymore.
 */
export interface PlacementController {
  readonly assignments: Readonly<Record<number, Zone | null>>;
  readonly activeCoinIndex: number | null;
  readonly allAssigned: boolean;
  readonly anyPlaced: boolean;
  readonly onCoinClick: (index: number) => void;
  readonly onCancel: () => void;
  readonly onReset: () => void;
  readonly onConfirm: () => void;
}

interface CoinHandProps {
  readonly coins: readonly Character[];
  readonly placement: PlacementController;
  readonly disabled: boolean;
}

function RefreshIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-2.6-6.4" />
      <path d="M21 4v5h-5" />
    </svg>
  );
}

export function CoinHand({ coins, placement, disabled }: CoinHandProps) {
  if (coins.length === 0) {
    return (
      <div
        className="p-4 text-center text-sm"
        style={{ color: "var(--cn-horror-text-muted)" }}
      >
        코인을 뽑을 차례를 기다리세요
      </div>
    );
  }

  const { assignments, activeCoinIndex, allAssigned, anyPlaced } = placement;

  return (
    <div data-testid="horror-card-hand" className="flex flex-col gap-3">
      <div className="flex items-start justify-center gap-6 pt-1">
        {coins.map((coin, index) => {
          const zone = assignments[index] ?? null;
          const isActive = activeCoinIndex === index;
          const isPlaced = !isActive && zone !== null;
          const state = isActive ? "active" : isPlaced ? "placed" : "unplaced";

          return (
            <button
              key={index}
              type="button"
              data-testid={`coin-chip-${index}`}
              data-character={coin}
              data-zone={zone ?? ""}
              data-state={state}
              aria-pressed={isActive}
              onClick={() => placement.onCoinClick(index)}
              disabled={disabled}
              className="relative cursor-pointer border-none bg-transparent p-0 transition-opacity disabled:cursor-not-allowed"
              style={{ opacity: isPlaced ? 0.5 : 1 }}
            >
              {/* ring wraps the icon + label as one set (not just the icon) */}
              {(isActive || state === "unplaced") && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute"
                  style={{
                    inset: "-4px -7px -4px -7px",
                    borderRadius: 12,
                    border: isActive
                      ? "2px solid var(--cn-horror-finished-flash)"
                      : "1.5px dashed var(--cn-horror-text-muted)",
                    boxShadow: isActive
                      ? "0 0 10px color-mix(in srgb, var(--cn-horror-finished-flash) 55%, transparent)"
                      : "none",
                  }}
                />
              )}
              <HorrorCharacterChip
                character={coin}
                iconSize={30}
                labelSize={11}
              />
              {isPlaced && zone !== null && (
                <span
                  data-testid={`coin-chip-${index}-badge`}
                  className="absolute font-bold text-[10px] leading-tight"
                  style={{
                    top: -7,
                    right: -13,
                    backgroundColor: "var(--cn-horror-zone-accent)",
                    color: "#1a0f1f",
                    borderRadius: 999,
                    padding: "0 5px",
                  }}
                >
                  +{zone}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* T23-b — fixed control bar: 초기화(↻) : 빈칸 : 취소 : 확정 at
          1:(1):1:1. Positions never change; inapplicable buttons disable in place. */}
      <div className="grid grid-cols-4 gap-2">
        <Button
          data-testid="placement-reset"
          variant="ghost"
          onClick={placement.onReset}
          disabled={disabled || !anyPlaced}
          aria-label="초기화"
          className="flex w-full items-center justify-center"
        >
          <RefreshIcon />
        </Button>
        <span aria-hidden="true" />
        <Button
          data-testid="placement-cancel"
          variant="ghost"
          onClick={placement.onCancel}
          disabled={disabled || activeCoinIndex === null}
          className="w-full"
        >
          취소
        </Button>
        <Button
          data-testid="confirm-placement-btn"
          variant="primary"
          onClick={placement.onConfirm}
          disabled={disabled || !allAssigned}
          className="w-full whitespace-nowrap"
        >
          확정
        </Button>
      </div>
    </div>
  );
}
